/**
 * MoneyMovement (revised) — grouped bars (Income + Spending) + amber
 * Net Cash Flow line + 3M/6M/12M range selector + 4-KPI strip below.
 *
 * Phase 84.20.6 (user follow-up on Beta #2 fix) — The amber line was
 * previously called "Money Kept", which reads as nonsensical when the
 * value is negative (you can't literally KEEP negative money). The line
 * is now labelled **Net Cash Flow** (Income − Spending), which is a
 * signed metric that gracefully accepts both directions. Per-month
 * detail on the tooltip AND on the KPI strip adapts contextually:
 *   • value ≥ 0 → "Money Kept"   (evergreen colour)
 *   • value < 0 → "Overspent"    (warm/danger colour, magnitude shown)
 * Chart dots also colour-code per-month: sage for kept months, warm-red
 * for overspent months. Everything is fully currency-aware via
 * `formatMoney(v, baseCurrency)` and `currencySymbol(baseCurrency)`.
 *
 * Data contract: `data12m` covers the full available window (12 months
 * in demo, whatever we have in real data). The range selector slices
 * that window; no additional network calls.
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { formatMoney, currencySymbol } from '@/lib/format';

const RANGES = [
  { key: '3M',  months: 3,  caption: 'over the last 3M.'  },
  { key: '6M',  months: 6,  caption: 'over the last 6M.'  },
  { key: '12M', months: 12, caption: 'over all available months.' },
];

// Sign-aware colour helpers. Positive = sage/evergreen (money kept),
// negative = warm red (overspent). These follow the existing token
// palette (sage / gold / muted-red) rather than raw hex values so
// dark-mode adapts automatically.
const POSITIVE_COLOR = 'hsl(var(--sage))';       // money kept
const NEGATIVE_COLOR = 'hsl(var(--destructive))'; // overspent

// Per-dot colour on the line: sage for kept months, red for overspent.
function ColoredDot({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  const v = payload?.money_kept_base ?? 0;
  const fill = v >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;
  return <circle cx={cx} cy={cy} r={4} fill={fill} stroke="hsl(var(--background))" strokeWidth={1.5} />;
}

function Tooltip_({ active, payload, label, baseCurrency }) {
  if (!active || !payload || payload.length === 0) return null;
  const income   = payload.find((p) => p.dataKey === 'income_base')?.value ?? 0;
  const spending = payload.find((p) => p.dataKey === 'expenses_base')?.value ?? 0;
  const netFlow  = payload.find((p) => p.dataKey === 'money_kept_base')?.value ?? 0;
  const isKept   = netFlow >= 0;
  // Phase 84.20.6 — Adaptive per-point label. When the month landed
  // positive we call it "Money Kept"; when negative we call it
  // "Overspent" and display the absolute magnitude. `formatMoney`
  // already handles the currency symbol + locale-aware separators
  // via `baseCurrency`, so `AED`, `GBP`, `INR`, etc. all render
  // correctly (e.g. "AED 303,108" instead of "₹303,108" for an AED
  // user).
  return (
    <div className="rounded-lg border border-border bg-popover px-4 py-3 shadow-lg text-xs">
      <div className="font-semibold text-foreground text-sm mb-2">{label}</div>
      <div className="space-y-1.5 min-w-[180px]">
        <div className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-full bg-sage" /> Income:</span>
          <span className="num text-foreground font-medium">{formatMoney(income, baseCurrency)}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-full bg-primary" /> Spending:</span>
          <span className="num text-foreground font-medium">{formatMoney(spending, baseCurrency)}</span>
        </div>
        <div className="flex items-center justify-between gap-6 pt-1 border-t border-border/50 mt-1">
          <span className="flex items-center gap-2 text-muted-foreground">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: isKept ? POSITIVE_COLOR : NEGATIVE_COLOR }}
            />
            {isKept ? 'Money Kept:' : 'Overspent:'}
          </span>
          <span
            className="num font-semibold"
            style={{ color: isKept ? POSITIVE_COLOR : NEGATIVE_COLOR }}
            data-testid="money-movement-tooltip-net"
          >
            {formatMoney(Math.abs(netFlow), baseCurrency)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function MoneyMovement({
  data = [],           // Array<{label, income_base, expenses_base, money_kept_base|surplus_base}>
  loading = false,
  baseCurrency = 'INR',
  excluded = null,     // excluded_counts from lib/interimPolicy.js
}) {
  const [range, setRange] = useState('6M');
  const active = RANGES.find((r) => r.key === range) || RANGES[1];

  const excludedNote = useMemo(() => {
    if (!excluded) return null;
    const parts = [];
    if (excluded.borrowedIncome > 0) {
      parts.push(`${excluded.borrowedIncome} borrowed-money ${excluded.borrowedIncome === 1 ? 'credit' : 'credits'} kept out of income`);
    }
    if (excluded.unresolvedInflow > 0) {
      // Cash received, meaning unconfirmed. Not income, not hidden.
      parts.push(`${excluded.unresolvedInflow} unconfirmed ${excluded.unresolvedInflow === 1 ? 'credit' : 'credits'} awaiting your review`);
    }
    if (excluded.refunds > 0) {
      parts.push(`${excluded.refunds} ${excluded.refunds === 1 ? 'refund' : 'refunds'} netted off spending`);
    }
    if (excluded.transfers > 0) {
      parts.push(`${excluded.transfers} ${excluded.transfers === 1 ? 'transfer' : 'transfers'} excluded`);
    }
    if (excluded.investments > 0) {
      parts.push(`${excluded.investments} investment ${excluded.investments === 1 ? 'contribution' : 'contributions'} excluded`);
    }
    return parts.length ? `${parts.join(' · ')}.` : null;
  }, [excluded]);

  const sliced = useMemo(() => {
    const norm = (data || []).map((d) => ({
      ...d,
      money_kept_base: d.money_kept_base ?? d.surplus_base ?? 0,
    }));
    return norm.slice(-active.months);
  }, [data, active.months]);

  const hasData = sliced.some((d) => d.income_base > 0 || d.expenses_base > 0);

  const latest = sliced[sliced.length - 1] || { income_base: 0, expenses_base: 0, money_kept_base: 0 };
  const netCashFlow = (latest.income_base || 0) - (latest.expenses_base || 0);

  return (
    <Card data-testid="dashboard-money-movement" className="border-border/70">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground">Your Money Movement</h3>
            {/* Phase 84.20.6 — Line renamed to "Net Cash Flow" so the
                metric survives negative months. Sub-copy explicitly
                names the three series so hovering the chart doesn't
                require guessing. */}
            <p className="text-xs text-muted-foreground mt-0.5">Income, spending, and net cash flow {active.caption}</p>
          </div>
          <div className="inline-flex rounded-full bg-muted p-1" role="tablist" data-testid="money-movement-range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={
                  'px-3 py-1 text-xs font-medium rounded-full transition-colors ' +
                  (r.key === range ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')
                }
                data-testid={`money-movement-range-${r.key.toLowerCase()}`}
                aria-pressed={r.key === range}
              >{r.key}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-56 w-full" />
        ) : !hasData ? (
          <div className="h-48 flex items-center justify-center" data-testid="money-movement-empty">
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Once you have at least one month of activity, your income vs spending trend will appear here.
            </p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={sliced} margin={{ top: 8, right: 12, left: 8, bottom: 0 }} barGap={4} barCategoryGap={20}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={56}
                  tickFormatter={(v) => {
                    // Phase 84.19 — Bug 1 fix. Historically the Y-axis
                    // hardcoded `₹` and lakh/crore formatting regardless
                    // of the user's base currency, so an AED user saw
                    // `₹50K` while every summary tile showed `AED ...`.
                    // Use the same currency symbol as the tiles and
                    // choose the compaction style the currency expects:
                    //   INR  → k / L / Cr
                    //   rest → k / M / B
                    const sym = currencySymbol(baseCurrency);
                    const isINR = (baseCurrency || '').toUpperCase() === 'INR';
                    if (isINR) {
                      if (v >= 1e7) return `${sym}${(v / 1e7).toFixed(1)}Cr`;
                      if (v >= 1e5) return `${sym}${(v / 1e5).toFixed(1)}L`;
                      if (v >= 1000) return `${sym}${Math.round(v / 1000)}K`;
                      return `${sym}${v}`;
                    }
                    if (v >= 1e9) return `${sym}${(v / 1e9).toFixed(1)}B`;
                    if (v >= 1e6) return `${sym}${(v / 1e6).toFixed(1)}M`;
                    if (v >= 1000) return `${sym}${Math.round(v / 1000)}K`;
                    return `${sym}${v}`;
                  }} />
                <Tooltip content={<Tooltip_ baseCurrency={baseCurrency} />} cursor={{ fill: 'hsl(var(--muted) / 0.4)' }} />
                {/* Phase 84.20.6 — zero-line reference. When any month
                    dips into overspending territory the amber line drops
                    below zero; the dotted reference makes that boundary
                    unambiguous. Rendered before the bars so it sits
                    behind them. */}
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="2 3" ifOverflow="extendDomain" />
                <Bar dataKey="income_base"   name="Income"   fill="hsl(var(--sage))"    radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses_base" name="Spending" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                {/* Phase 84.20.6 — Line renamed to "Net Cash Flow".
                    Per-dot colour: sage for kept months, warm-red for
                    overspent months (see ColoredDot at top of file). */}
                <Line type="monotone" dataKey="money_kept_base" name="Net Cash Flow"
                  stroke="hsl(var(--gold))" strokeWidth={2}
                  dot={<ColoredDot />}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: 'hsl(var(--background))' }} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex items-center gap-5 mt-3 text-xs text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-sage" /> Income</span>
              <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> Spending</span>
              {/* Phase 84.20.6 — line legend renamed; sub-copy names the
                  two per-point states so users know what to expect when
                  hovering. */}
              <span className="inline-flex items-center gap-2">
                <span className="relative h-2.5 w-4 inline-flex items-center">
                  <span className="absolute top-1/2 -translate-y-1/2 left-0 h-px w-4 bg-gold" />
                  <span className="absolute top-1/2 -translate-y-1/2 left-1.5 h-2 w-2 rounded-full bg-gold" />
                </span>
                Net Cash Flow <span className="opacity-70">(money kept / overspent per month)</span>
              </span>
            </div>

            {/* 4-KPI strip */}
            <div className="mt-5 pt-4 border-t border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="money-movement-kpis">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Income this month</div>
                <div className="num text-lg font-semibold text-foreground mt-1" data-testid="kpi-income">{formatMoney(latest.income_base || 0, baseCurrency)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Spent this month</div>
                <div className="num text-lg font-semibold text-foreground mt-1" data-testid="kpi-spent">{formatMoney(latest.expenses_base || 0, baseCurrency)}</div>
              </div>
              {/* Phase 84.20.6 — This tile adapts to the sign of the
                  current-month cash flow. "Money kept" (evergreen) when
                  positive; "Overspent" (warm-red) with magnitude when
                  negative. Prevents the historical bug of showing a
                  red-flag number under a soothing green "Money kept"
                  label. */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {(latest.money_kept_base || 0) >= 0 ? 'Money kept' : 'Overspent'}
                </div>
                <div
                  className="num text-lg font-semibold mt-1"
                  style={{ color: (latest.money_kept_base || 0) >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR }}
                  data-testid="kpi-kept"
                >
                  {formatMoney(Math.abs(latest.money_kept_base || 0), baseCurrency)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Net cash flow</div>
                <div
                  className="num text-lg font-semibold mt-1"
                  style={{ color: netCashFlow >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR }}
                  data-testid="kpi-net"
                >
                  {netCashFlow >= 0 ? '+' : '−'}{formatMoney(Math.abs(netCashFlow), baseCurrency)}
                </div>
              </div>
            </div>

            {/* Phase 1 containment — disclose what these totals left out.
                Borrowed money that arrives as "income", refunds netted off
                spending, transfers and investments. Rendered only when
                something was actually excluded. */}
            {excludedNote && (
              <p className="mt-3 text-[11px] text-muted-foreground" data-testid="money-movement-excluded-note">
                {excludedNote}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
